import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface FleetVehicle {
  id?: string | number;
  truck_number?: string;
  current_status?: string;
  lease_type?: string;
  has_rental?: boolean;
  terminal_name?: string;
  current_terminal_name?: string;
  current_terminal_id?: string | number;
  vin?: string;
  license_plate?: string;
  year?: number;
  make?: string;
  model?: string;
  last_known_mileage?: number;
  current_mileage?: number;
  issue_count?: number;
  oos_reason?: string;
  rental_vendor?: string;
  expiration_date?: string;
  recent_issues?: FleetIssue[];
}

export interface FleetIssue {
  id: string | number;
  status?: string;
  date?: string;
  issue_description?: string;
}

export interface FleetTerminal {
  id: string | number;
  name?: string;
  code?: string;
}

export interface TerminalOption {
  id: string;
  name: string;
}

export interface FleetStatusCounts {
  all: number;
  active: number;
  oos: number;
  in_transit: number;
  rental: number;
  issues: number;
}

interface FleetResponse {
  vehicles?: FleetVehicle[];
  terminals?: FleetTerminal[];
  error?: string;
}

export function useFleetReadModel(statusFilter: string, terminalFilter: string) {
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [terminals, setTerminals] = useState<FleetTerminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (terminalFilter && terminalFilter !== 'all') params.set('terminal_id', terminalFilter);

      const data = await apiFetch<FleetResponse>(`/api/fleet/vehicles?${params.toString()}`);
      if (data?.error) throw new Error(data.error);

      setVehicles(data.vehicles || []);
      setTerminals(data.terminals || []);
    } catch (err) {
      setVehicles([]);
      setTerminals([]);
      setError(err instanceof Error ? err.message : 'Fleet read load failed');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, terminalFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const terminalOptions = useMemo<TerminalOption[]>(() => {
    const fromApi = terminals.map((t) => ({ id: String(t.id), name: t.name || t.code || `Terminal ${t.id}` }));
    if (fromApi.length) return [{ id: 'all', name: 'All terminals' }, ...fromApi];

    const seen = new Map<string, TerminalOption>();
    vehicles.forEach((v) => {
      const id = v.current_terminal_id;
      const name = v.terminal_name || v.current_terminal_name;
      if (!id || seen.has(String(id))) return;
      seen.set(String(id), { id: String(id), name: name || `Terminal ${id}` });
    });
    return [{ id: 'all', name: 'All terminals' }, ...Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))];
  }, [terminals, vehicles]);

  const statusCounts = useMemo<FleetStatusCounts>(() => {
    const counts: FleetStatusCounts = { all: vehicles.length, active: 0, oos: 0, in_transit: 0, rental: 0, issues: 0 };
    vehicles.forEach((v) => {
      if (v.current_status === 'active') counts.active++;
      if (v.current_status === 'oos') counts.oos++;
      if (v.current_status === 'in_transit') counts.in_transit++;
      if (v.lease_type === 'rental' || v.has_rental) counts.rental++;
      if ((v.issue_count || 0) > 0) counts.issues++;
    });
    return counts;
  }, [vehicles]);

  const vehiclesWithIssues = useMemo(() => vehicles.filter((v) => (v.issue_count || 0) > 0), [vehicles]);
  const totalOpenIssues = useMemo(() => vehicles.reduce((sum, v) => sum + (v.issue_count || 0), 0), [vehicles]);

  return {
    vehicles,
    terminals,
    terminalOptions,
    statusCounts,
    vehiclesWithIssues,
    totalOpenIssues,
    loading,
    error,
    refreshFleet: load,
  };
}
